import React, { forwardRef, HTMLAttributes, useCallback, useEffect, useImperativeHandle, useRef } from "react";

interface Props {
    onVisibilityChange?: (isVisible: boolean) => void;
}

enum ELEMENT_VISIBILITY_STATE {
    TO_INIT,
    VISIBLE,
    INVISIBLE,
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ElementVisibilitySensor(props: Props & HTMLAttributes<HTMLDivElement>, ref: any): JSX.Element {
    const { onVisibilityChange, ...restProps } = props as Props;

    const elementWasVisibleGlobal = useRef(ELEMENT_VISIBILITY_STATE.TO_INIT);
    const elementRef = useRef<HTMLDivElement>(null);
    const ioGlobal = useRef<IntersectionObserver>();

    useImperativeHandle(ref, () => ({
        getElementRef: (): HTMLDivElement | null => {
            return elementRef.current;
        },
    }));

    const handleIntersection: IntersectionObserverCallback = useCallback(
        (entries, _) => {
            entries.forEach(entry => {
                const elementIsVisible =
                    entry.isIntersecting || entry.intersectionRatio > 0
                        ? ELEMENT_VISIBILITY_STATE.VISIBLE
                        : ELEMENT_VISIBILITY_STATE.INVISIBLE;

                if (elementIsVisible !== elementWasVisibleGlobal.current) {
                    elementWasVisibleGlobal.current = elementIsVisible;
                    if (props.onVisibilityChange) {
                        props.onVisibilityChange(elementIsVisible === ELEMENT_VISIBILITY_STATE.VISIBLE);
                    }
                }
            });
        },
        [props],
    );

    useEffect(() => {
        if (window !== undefined && IntersectionObserver !== undefined) {
            const io = new IntersectionObserver(handleIntersection);

            if (elementRef?.current != null) {
                io.observe(elementRef.current as HTMLDivElement);
            }

            ioGlobal.current = io;
        } else {
            if (props.onVisibilityChange) {
                props.onVisibilityChange(true);
            }
        }

        return (): void => {
            if (ioGlobal.current) {
                ioGlobal.current.disconnect();
            }
        };
    }, [elementRef, handleIntersection, props]);

    return <div {...restProps} ref={elementRef} />;
}

export default forwardRef(ElementVisibilitySensor);
